<script setup lang="ts">
  import { CalendarDate } from '@internationalized/date'
  import { parseISO, format } from 'date-fns'

  const emit = defineEmits(['update:modelValue'])

  const props = defineProps({
    time: { type: Boolean, default: false },
    max: { type: Date, default: () => new Date() },
    modelValue: { type: String, default: '' },
    clear: { type: Boolean, default: false },
  })

  const display = ref('')
  const isOpen = ref(false)
  const inputRef = ref()
  const { locale } = useI18n()
  const calendarValue = ref()
  const timePart = ref('00:00')

  const close = () => {
    isOpen.value = false
  }

  const displayWithTime = computed(() =>
    props.modelValue ? `${display.value} - ${timePart.value}` : ''
  )
  const displayValue = computed(() =>
    props.time ? displayWithTime.value : display.value
  )
  const isClearBtn = computed(() => props.clear && props.modelValue)
  const iconNamePerClean = computed(() =>
    isClearBtn.value
      ? 'carbon:close-outline'
      : 'material-symbols:calendar-month-outline'
  )

  const onIconClick = () => {
    if (isOpen.value) {
      close()
      return
    }
    if (isClearBtn.value) {
      emit('update:modelValue', null)
      if (props.time) timePart.value = '00:00'
    }
    isOpen.value = true
  }

  function parseModelValue(val: string | null | undefined) {
    if (!val) return null
    const parsed = parseISO(val)
    return isNaN(parsed.getTime()) ? null : parsed
  }

  watch(
    () => props.modelValue,
    (val) => {
      if (!val) {
        calendarValue.value = null
        display.value = ''
        timePart.value = '00:00'
        return
      }
      const parsed = parseISO(val)
      calendarValue.value = new CalendarDate(
        parsed.getFullYear(),
        parsed.getMonth() + 1,
        parsed.getDate()
      )
      display.value = format(parsed, 'dd/MM/yyyy')
      if (props.time) timePart.value = format(parsed, 'HH:mm')
    },
    { immediate: true }
  )

  watch(calendarValue, (val) => {
    if (!val) {
      emit('update:modelValue', '')
      display.value = ''
      return
    }

    let hours = 0,
      minutes = 0
    if (props.time) [hours, minutes] = timePart.value.split(':').map(Number)

    const d = new Date(val.year, val.month - 1, val.day, hours, minutes)
    if (isNaN(d.getTime())) return

    emit(
      'update:modelValue',
      props.time ? format(d, 'yyyy-MM-dd HH:mm:ss') : format(d, 'yyyy-MM-dd')
    )
    display.value = format(d, 'dd/MM/yyyy')
    if (!props.time) close()
  })

  watch(timePart, (val) => {
    if (!props.time || !props.modelValue) return
    const parsed = parseModelValue(props.modelValue)
    if (!parsed) return
    const [h, m] = val.split(':').map(Number)
    parsed.setHours(h)
    parsed.setMinutes(m)
    emit('update:modelValue', format(parsed, 'yyyy-MM-dd HH:mm:ss'))
  })
</script>

<template>
  <UPopover v-model:open="isOpen" :ui="{ content: 'w-full' }">
    <div ref="inputRef" class="w-full cursor-pointer">
      <UInput
        :value="displayValue"
        readonly
        class="w-full"
        :ui="{ base: 'py-2 px-4' }"
      >
        <template #trailing>
          <span
            class="absolute inset-y-0 end-0 flex cursor-pointer items-center pe-2.5"
            @click.stop="onIconClick"
          >
            <Icon :name="iconNamePerClean" />
          </span>
        </template>
      </UInput>
    </div>

    <template #content>
      <div class="flex flex-col gap-2 p-2">
        <UCalendar
          v-model="calendarValue"
          class="cursor-pointer"
          :max-date="max"
          :locale="locale"
        />

        <div v-if="time" class="flex justify-between gap-2">
          <UInput v-model="timePart" type="time" class="flex-1" />

          <Button @click="close">Принять</Button>
        </div>
      </div>
    </template>
  </UPopover>
</template>
